import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteFaq, useUpdateFaq } from "@/features/faq/hooks/use-faq";
import type { Faq } from "@/features/faq/types";
import { useTranslation } from "@/features/utils/i18n";
import {
  ArchiveIcon,
  CopyIcon,
  DotsThreeOutlineIcon,
  PencilSimpleIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";
import { RefineFaqDialog } from "./refine-dialog";

export const Action = ({
  faq,
  categories = [],
}: {
  faq: Faq;
  categories?: string[];
}) => {
  const { t } = useTranslation("faq");
  const deleteFaq = useDeleteFaq();
  const updateFaq = useUpdateFaq();
  const [refineOpen, setRefineOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteFaq.mutateAsync(faq.id);
      toast.success(t("messages.deleteSuccess"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.deleteError"),
      );
    }
  };

  const handleArchive = async () => {
    try {
      await updateFaq.mutateAsync({
        id: faq.id,
        data: { status: faq.status === "archived" ? "active" : "archived" },
      });
      toast.success(t("messages.saveSuccess"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.saveError"),
      );
    }
  };

  return (
    <div className="flex items-center justify-end gap-1">
      <Button
        variant="ghost"
        size="sm"
        className="h-8"
        onClick={() => setRefineOpen(true)}
      >
        <PencilSimpleIcon className="h-4 w-4" />
        {t("actions.review")}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">{t("table.actions")}</span>
            <DotsThreeOutlineIcon weight="fill" className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setRefineOpen(true)}>
            <PencilSimpleIcon className="mr-2 h-4 w-4" />
            {t("actions.refine")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => navigator.clipboard.writeText(faq.id)}
          >
            <CopyIcon className="mr-2 h-4 w-4" />
            {t("actions.copyId")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleArchive}>
            <ArchiveIcon className="mr-2 h-4 w-4" />
            {faq.status === "archived"
              ? t("actions.unarchive")
              : t("actions.archive")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="p-0">
            <Button
              variant="ghost"
              onClick={handleDelete}
              className="text-destructive hover:text-destructive w-full justify-start"
              confirm={t("messages.deleteConfirm", { question: faq.question })}
            >
              <TrashIcon className="mr-2 h-4 w-4" />
              {t("actions.delete")}
            </Button>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <RefineFaqDialog
        faq={faq}
        categories={categories}
        open={refineOpen}
        onOpenChange={setRefineOpen}
      />
    </div>
  );
};
